import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import {
  Send,
  Reply,
  CalendarCheck,
  MessageCircle,
  Sparkles,
  Loader2,
  Check,
  Sun,
  Plus,
  Flame,
  Trophy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useApi } from '../api/client';
import { useStreak } from '../api/useStreak';
import { useQuickLog } from '../components/QuickLogProvider';

interface Metric {
  today: number;
  week: number;
  daily: number | null;
  weekly: number | null;
}

type MetricKey =
  | 'outreaches'
  | 'follow_ups'
  | 'legacy_chats_scheduled'
  | 'legacy_chats_completed'
  | 'discovery_calls';

interface DashboardData {
  today: string;
  week_start: string;
  metrics: Record<MetricKey, Metric>;
}

type Tone = 'lime' | 'mint' | 'sky' | 'coral' | 'sun' | 'violet';

interface TileSpec {
  key: MetricKey;
  label: string;
  icon: LucideIcon;
  tone: Tone;
}

// What does the inline "+" button do for each daily tile?
//   'outreach' / 'follow_up'  → open QuickLog modal preselecting kind
//   undefined                 → no inline + (pipeline tiles)
type LogAction = 'outreach' | 'follow_up';

const TONE_CLASSES: Record<
  Tone,
  { pillBg: string; pillText: string; bar: string; count: string; ring: string }
> = {
  lime: {
    pillBg: 'bg-brand-lime',
    pillText: 'text-brand-forest',
    bar: 'bg-brand-lime',
    count: 'text-brand-forest',
    ring: 'ring-brand-lime/30',
  },
  mint: {
    pillBg: 'bg-brand-mint',
    pillText: 'text-brand-forest',
    bar: 'bg-brand-mint',
    count: 'text-brand-forest',
    ring: 'ring-brand-mint/40',
  },
  sky: {
    pillBg: 'bg-brand-sky',
    pillText: 'text-brand-forest',
    bar: 'bg-brand-sky-600',
    count: 'text-brand-forest',
    ring: 'ring-brand-sky/40',
  },
  coral: {
    pillBg: 'bg-brand-coral',
    pillText: 'text-white',
    bar: 'bg-brand-coral',
    count: 'text-brand-coral-600',
    ring: 'ring-brand-coral/40',
  },
  sun: {
    pillBg: 'bg-brand-sun',
    pillText: 'text-brand-forest',
    bar: 'bg-brand-sun',
    count: 'text-brand-sun-600',
    ring: 'ring-brand-sun/40',
  },
  violet: {
    pillBg: 'bg-brand-violet',
    pillText: 'text-white',
    bar: 'bg-brand-violet',
    count: 'text-brand-violet-600',
    ring: 'ring-brand-violet/40',
  },
};

interface DailyTileSpec extends TileSpec {
  action: LogAction;
}

const DAILY_TILES: DailyTileSpec[] = [
  { key: 'outreaches', label: 'Outreaches', icon: Send, tone: 'lime', action: 'outreach' },
  { key: 'follow_ups', label: 'Follow-ups', icon: Reply, tone: 'coral', action: 'follow_up' },
];

const PIPELINE_TILES: TileSpec[] = [
  { key: 'legacy_chats_scheduled', label: 'Legacy Chats Scheduled', icon: CalendarCheck, tone: 'mint' },
  { key: 'legacy_chats_completed', label: 'Legacy Chats Completed', icon: MessageCircle, tone: 'sky' },
  { key: 'discovery_calls', label: 'Discovery Calls', icon: Sparkles, tone: 'violet' },
];

interface SectionSpec {
  title: string;
  icon: LucideIcon;
  tone: Tone;
}

function ProgressTile({
  label,
  count,
  goal,
  icon: Icon,
  tone,
  onAdd,
}: {
  label: string;
  count: number;
  goal: number | null;
  icon: LucideIcon;
  tone: Tone;
  onAdd?: () => void;
}) {
  const target = goal ?? 0;
  const pct = target > 0 ? Math.min(100, (count / target) * 100) : 0;
  const complete = target > 0 && count >= target;
  const c = TONE_CLASSES[tone];

  return (
    <motion.div
      layout
      whileHover={{ y: -3 }}
      className={[
        'relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition',
        complete
          ? 'border-brand-lime ring-2 ring-brand-lime/40 shadow-md'
          : `border-brand-sage/20 ring-1 ${c.ring}`,
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className={[
            'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-wide shadow-sm',
            c.pillBg,
            c.pillText,
          ].join(' ')}
        >
          <Icon className="h-4 w-4" strokeWidth={2.5} />
          {label}
        </div>
        <div className="flex items-center gap-1.5">
          {onAdd && (
            <motion.button
              type="button"
              onClick={onAdd}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              transition={{ duration: 0.15 }}
              className="grid h-7 w-7 place-items-center rounded-full bg-brand-forest text-brand-paper shadow-sm transition hover:bg-brand-lime hover:text-brand-forest"
              title={`Log a ${label.toLowerCase().replace(/s$/, '')}`}
            >
              <Plus className="h-4 w-4" strokeWidth={3} />
            </motion.button>
          )}
          {complete && (
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              className="grid h-7 w-7 place-items-center rounded-full bg-brand-lime text-brand-forest shadow"
            >
              <Check className="h-4 w-4" strokeWidth={3.5} />
            </motion.div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <motion.span
          key={count}
          initial={{ scale: 0.85, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 24 }}
          className={[
            'font-display text-5xl font-black leading-none',
            complete ? 'text-brand-lime-700' : c.count,
          ].join(' ')}
        >
          {count}
        </motion.span>
        <span className="text-base font-bold text-brand-sage">
          / {goal ?? '—'}
        </span>
      </div>

      <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-brand-sage/15">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ type: 'spring', stiffness: 80, damping: 20 }}
          className={[
            'h-full rounded-full',
            complete ? 'bg-brand-lime' : c.bar,
          ].join(' ')}
        />
      </div>
    </motion.div>
  );
}

function SectionHeading({
  title,
  icon: Icon,
  tone,
  subtitle,
}: SectionSpec & { subtitle?: string }) {
  const c = TONE_CLASSES[tone];
  return (
    <div className="mb-4 flex items-center gap-3">
      <div
        className={[
          'grid h-9 w-9 place-items-center rounded-xl shadow-sm',
          c.pillBg,
          c.pillText,
        ].join(' ')}
      >
        <Icon className="h-5 w-5" strokeWidth={2.5} />
      </div>
      <div>
        <h2 className="font-display text-2xl font-black leading-none text-brand-forest">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-xs font-bold uppercase tracking-wider text-brand-sage">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

function StreakBadge({
  current,
  best,
  status,
}: {
  current: number;
  best: number;
  status: 'complete' | 'in_progress' | 'not_started';
}) {
  // Visual tone reflects whether today is putting the streak at risk.
  const flameClass =
    status === 'complete'
      ? 'text-brand-sun'
      : status === 'in_progress'
        ? 'text-brand-coral-100'
        : 'text-brand-sage';
  const subtitle =
    status === 'complete'
      ? 'Streak locked in for today'
      : current === 0
        ? 'Start your streak — finish all daily targets'
        : status === 'in_progress'
          ? 'Keep going — finish today to extend it'
          : 'At risk — finish today to keep it';

  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-3 rounded-2xl bg-brand-forest-700 px-4 py-3 shadow-inner ring-1 ring-brand-forest-900/40"
    >
      <motion.div
        animate={
          status === 'complete'
            ? { rotate: [0, -8, 8, -6, 0], scale: [1, 1.1, 1] }
            : {}
        }
        transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 2.4 }}
      >
        <Flame className={`h-9 w-9 ${flameClass}`} strokeWidth={2.5} />
      </motion.div>
      <div>
        <div className="flex items-baseline gap-1">
          <span className="font-display text-3xl font-black leading-none text-white">
            {current}
          </span>
          <span className="text-xs font-bold uppercase tracking-wider text-brand-sky">
            day{current === 1 ? '' : 's'}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-brand-mint-100">{subtitle}</p>
        {best > current && best > 0 && (
          <p className="mt-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-brand-sun">
            <Trophy className="h-3 w-3" />
            Best {best}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function fireConfetti() {
  confetti({
    particleCount: 100,
    spread: 80,
    origin: { y: 0.6 },
    colors: ['#A1CD3C', '#80C99B', '#9CD9DE', '#FF7A5C', '#F4B942', '#F472B6', '#A78BFA'],
  });
}

export default function Dashboard() {
  const apiFetch = useApi();
  const { streak, reload: reloadStreak } = useStreak();
  const { open: openQuickLogModal, onLogged } = useQuickLog();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completed = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);

  const refreshAll = useCallback(async () => {
    try {
      const r = await apiFetch('/dashboard/today');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: DashboardData = await r.json();
      setData(d);
      if (!initialLoadDone.current) {
        const seen = completed.current;
        for (const key of Object.keys(d.metrics) as MetricKey[]) {
          const m = d.metrics[key];
          if (m.daily != null && m.today >= m.daily) seen.add(`${key}-daily`);
          if (m.weekly != null && m.week >= m.weekly) seen.add(`${key}-weekly`);
        }
        initialLoadDone.current = true;
      }
      reloadStreak();
    } catch (e) {
      setError(String(e));
    }
  }, [apiFetch, reloadStreak]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!data) return;
    const seen = completed.current;
    let popped = false;
    for (const key of Object.keys(data.metrics) as MetricKey[]) {
      const m = data.metrics[key];
      const dailyKey = `${key}-daily`;
      const weeklyKey = `${key}-weekly`;
      if (m.daily != null && m.today >= m.daily && !seen.has(dailyKey)) {
        seen.add(dailyKey);
        popped = true;
      }
      if (m.weekly != null && m.week >= m.weekly && !seen.has(weeklyKey)) {
        seen.add(weeklyKey);
        popped = true;
      }
    }
    if (popped) fireConfetti();
  }, [data]);

  function openQuickLog(kind: 'initial' | 'follow_up' | undefined) {
    openQuickLogModal(kind);
  }

  // Refresh dashboard counts whenever any page (incl. the floating FAB)
  // logs a successful outreach.
  useEffect(() => {
    return onLogged(() => {
      refreshAll();
    });
  }, [onLogged, refreshAll]);

  function handleAddClick(action: LogAction) {
    if (action === 'outreach') openQuickLog('initial');
    else if (action === 'follow_up') openQuickLog('follow_up');
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-brand-sage">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading dashboard…</span>
      </div>
    );
  }

  const m = data.metrics;
  const todayLabel = new Date(data.today + 'T00:00:00').toLocaleDateString(
    undefined,
    { weekday: 'long', month: 'long', day: 'numeric' },
  );

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-4 rounded-2xl bg-gradient-to-r from-brand-forest via-brand-forest-700 to-brand-sage p-6 text-brand-paper shadow-md sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-sky">
            {todayLabel}
          </p>
          <h1 className="mt-1 font-display text-4xl font-black tracking-tight text-white">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-brand-mint-100">
            One outreach at a time. Make today count.
          </p>
        </div>
        {streak && streak.has_daily_goals && (
          <StreakBadge
            current={streak.current_streak}
            best={streak.best_streak}
            status={streak.today_status}
          />
        )}
      </div>

      <section>
        <SectionHeading
          title="Today's checklist"
          icon={Sun}
          tone="sun"
          subtitle="tap + to log one"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {DAILY_TILES.map(({ key, label, icon, tone, action }) => (
            <ProgressTile
              key={`today-${key}`}
              label={label}
              icon={icon}
              tone={tone}
              count={m[key]?.today ?? 0}
              goal={m[key]?.daily ?? null}
              onAdd={() => handleAddClick(action)}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading
          title="This week"
          icon={CalendarCheck}
          tone="mint"
          subtitle={`since ${data.week_start}`}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {DAILY_TILES.map(({ key, label, icon, tone }) => (
            <ProgressTile
              key={`week-${key}`}
              label={label}
              icon={icon}
              tone={tone}
              count={m[key]?.week ?? 0}
              goal={m[key]?.weekly ?? null}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading title="Pipeline" icon={Sparkles} tone="violet" subtitle="this week" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PIPELINE_TILES.map(({ key, label, icon, tone }) => (
            <ProgressTile
              key={`pipeline-${key}`}
              label={label}
              icon={icon}
              tone={tone}
              count={m[key]?.week ?? 0}
              goal={m[key]?.weekly ?? null}
            />
          ))}
        </div>
      </section>
    </div>
  );
}